import {
    closestCenter,
    type CollisionDetection,
    DndContext,
    type DragEndEvent,
    type DragOverEvent,
    DragOverlay,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    pointerWithin,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import clsx from "clsx";
import { ArrowRightLeft, FolderOpen, GripVertical, Package, Plus, Puzzle, Search, Trash2, TriangleAlert } from "lucide-react";
import { Link } from "wouter";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useConfirm } from "../../web/components/Confirm";
import { plural } from "../../shared/words";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { DraftFields, SelectField, TextField } from "../../web/components/fields";
import { Badge, DraftBar, Panel, TONE_TEXT } from "../../web/components/ui";
import { can, usePriceListSaver } from "./data";
import type { ListEntry, ListTag } from "./itemTags";
import { type MaterialRow, meta, type PriceListData, TAG_NAME_MAX, type TagRow, type TagSetRow, type Wording } from "./meta";
import { ServiceM8Name } from "./NameTags";
import { deriveConfig, havingSeveral, inOrder, smartName, tagIdsOf, wordingText } from "./smartTags";
import { matchesSearch } from "./ListToolbar";
import { TagChip, WORDING_TEXT } from "./TagPicker";
import { tagsByItem } from "./productTags";

/*
 * Tags (itemTags.ts, smartTags.ts): every one, items' and products', in one list in Settings › Price List (new ones products' while
 * only theirs are showing, else items'), each marked with its kind and narrowed to one to look through: the order is the
 * order names put their wording in, so it's dragged as one. The business's tags and tag sets in order, each with how many items
 * have it, changed as one draft and saved together (Save or Cancel along the bottom; leaving asks first). Drag them by their
 * handles to reorder, and tags into and out of sets. Open one to change it: its name, what it puts in names (in brackets,
 * includes or excludes, or a line), or delete it; a set too: pick one (an item has one of
 * its tags at most) or any, and Add tag. A one-only set that would give an item two of its tags is marked (saved, those items need
 * attention). Saving says first what it does to items (tags taken off or moved, clashes). Items are tagged from
 * their own windows; names that then read differently show as names to update.
 */

/** A tag in the draft: `key` its id, or a made-up one while it's new (`id` null). */
interface DTag {
    key: string;
    id: string | null;
    name: string;
    wording: Wording | null;
    /** Not in a set: it goes on products (productTags.ts). */
    forProducts: boolean;
}

/** A set in the draft, its tags in it in order. */
interface DSet {
    key: string;
    id: string | null;
    name: string;
    oneOnly: boolean;
    wording: Wording | null;
    /** Its tags go on products (productTags.ts). */
    forProducts: boolean;
    tags: DTag[];
}

type DEntry = { set: DSet } | { tag: DTag };

/** The list's own drop area (the tags not in a set and the sets), apart from each set's (`in:` its key). */
const ROOT = "root";
const keyOf = (e: DEntry) => ("set" in e ? e.set.key : e.tag.key);
const newKey = () => `new:${crypto.randomUUID()}`;

/** The list as saved. */
function listOf(data: Pick<PriceListData, "tags" | "tagSets">): DEntry[] {
    const tag = (t: TagRow): DTag => ({ key: t.id, id: t.id, name: t.name, wording: t.wording, forProducts: t.forProducts });
    return inOrder(data).map((e): DEntry => {
        if (!e.set) return { tag: tag(e.tags[0]) };
        const s = e.set;
        return { set: { key: s.id, id: s.id, name: s.name, oneOnly: s.oneOnly, wording: s.wording, forProducts: s.forProducts, tags: e.tags.map(tag) } };
    });
}

/** Every tag in a list, with the set it's in (or null). */
const tagsIn = (list: readonly DEntry[]) => list.flatMap((e) => ("set" in e ? e.set.tags.map((tag) => ({ tag, set: e.set as DSet | null })) : [{ tag: e.tag, set: null }]));
const setsIn = (list: readonly DEntry[]) => list.flatMap((e) => ("set" in e ? [e.set] : []));

/** A list as rows (smartTags.ts reads them): ids their keys, earlier wording as saved. */
function rowsOf(list: readonly DEntry[], data: Pick<PriceListData, "tags" | "tagSets">) {
    const aliases = new Map([...data.tags, ...data.tagSets].map((x) => [x.id, x.aliases]));
    const tags: TagRow[] = [];
    const tagSets: TagSetRow[] = [];
    list.forEach((e, position) => {
        const tag = (t: DTag, setId: string | null, at: number) => tags.push({ id: t.key, name: t.name, setId, wording: t.wording, forProducts: t.forProducts, aliases: aliases.get(t.key) ?? [], position: at });
        if ("tag" in e) return tag(e.tag, null, position);
        const s = e.set;
        tagSets.push({ id: s.key, name: s.name, oneOnly: s.oneOnly, wording: s.wording, forProducts: s.forProducts, aliases: aliases.get(s.key) ?? [], position });
        s.tags.forEach((t, i) => tag(t, s.key, i));
    });
    return { tags, tagSets };
}

/** What's sent to be saved (itemTags.ts saveTagList). */
const entriesOf = (list: readonly DEntry[]): ListEntry[] => {
    const tag = (t: DTag): ListTag => ({ id: t.id, name: t.name.trim(), wording: t.wording, forProducts: t.forProducts });
    return list.map((e) =>
        "tag" in e
            ? { tag: tag(e.tag) }
            : { set: { id: e.set.id, name: e.set.name.trim(), oneOnly: e.set.oneOnly, wording: e.set.wording, forProducts: e.set.forProducts, tags: e.set.tags.map(tag) } },
    );
};

/** A tag taken from wherever it is and put in `to` (a set's key, or ROOT), before `before` (a key there; none: last). */
function moveTag(list: readonly DEntry[], key: string, to: string, before: string | null): DEntry[] {
    let moving: DTag | undefined;
    const rest = list.flatMap((e): DEntry[] => {
        if ("tag" in e) {
            if (e.tag.key !== key) return [e];
            moving = e.tag;
            return [];
        }
        const found = e.set.tags.find((t) => t.key === key);
        if (!found) return [e];
        moving = found;
        return [{ set: { ...e.set, tags: e.set.tags.filter((t) => t.key !== key) } }];
    });
    const tag = moving;
    if (!tag) return [...list];
    const at = <T,>(items: T[], i: number, item: T) => [...items.slice(0, i < 0 ? items.length : i), item, ...items.slice(i < 0 ? items.length : i)];
    if (to === ROOT) return at(rest, before ? rest.findIndex((e) => keyOf(e) === before) : -1, { tag });
    return rest.map((e) => ("set" in e && e.set.key === to ? { set: { ...e.set, tags: at(e.set.tags, before ? e.set.tags.findIndex((t) => t.key === before) : -1, tag) } } : e));
}

const WORDING_KINDS: { value: string; label: string }[] = [
    { value: "", label: "Nothing" },
    { value: "brackets", label: "In brackets" },
    { value: "includes", label: "Includes" },
    { value: "excludes", label: "Excludes" },
    { value: "line", label: "Line under the name" },
];

/** A wording with its kind changed (its text kept). */
function withKind(w: Wording | null, kind: string, fallback: string): Wording | null {
    return kind ? { kind: kind as Wording["kind"], text: w?.text ?? fallback } : null;
}

/** The wording field's label for each kind. */
const TEXT_LABEL: Record<Wording["kind"], string> = { brackets: "Wording", includes: "What it includes", excludes: "What it excludes", line: "Line" };

/** What a tag or set puts in names: its kind and its text (a line can have brackets in it). */
function WordingFields(props: Readonly<{ wording: Wording | null; name: string; disabled: boolean; hint?: string; onChange: (w: Wording | null) => void }>) {
    const w = props.wording;
    return (
        <>
            <SelectField
                label="Adds to item names"
                value={w?.kind ?? ""}
                options={WORDING_KINDS}
                disabled={props.disabled}
                hint={props.hint}
                onSave={async (kind) => props.onChange(withKind(w, kind, props.name))}
            />
            {w ? (
                <TextField
                    label={TEXT_LABEL[w.kind]}
                    value={w.text}
                    disabled={props.disabled}
                    live
                    validate={(v) => {
                        if (!v.trim()) return "Some wording";
                        // A line can say anything (lead times in brackets and all); in the brackets, brackets would end them.
                        return w.kind !== "line" && /[()]/.test(v) ? "No brackets: it goes in the name's" : null;
                    }}
                    onSave={async (text) => props.onChange({ ...w, text })}
                />
            ) : null}
        </>
    );
}

const EXAMPLE = { uuid: "example", name: "Example item" };

/**
 * The name an item would have with a tag (its wording, and its set's), or with any of a set's tags, as the draft has them: worked
 * out as an item's is (smartTags.ts smartName: merged, "Includes Installation & Keys"), and shown as its full name is (NameTags.tsx
 * NamePreview). None when it adds nothing.
 */
function ExampleName({ rows, retired, tagKey, setKey }: Readonly<{ rows: ReturnType<typeof rowsOf>; retired: PriceListData["nameTags"]["retired"]; tagKey?: string; setKey?: string }>) {
    // A set: as if an item had a tag of its with no wording of its own.
    const tags = setKey ? [...rows.tags, { id: EXAMPLE.uuid, name: "", setId: setKey, wording: null, forProducts: false, aliases: [], position: 0 }] : rows.tags;
    const on = tagKey ?? EXAMPLE.uuid;
    const nameTags = deriveConfig({ tags, tagSets: rows.tagSets }, retired);
    const name = smartName({ tags, tagSets: rows.tagSets, nameTags, itemTags: { [EXAMPLE.uuid]: [on] }, materials: [EXAMPLE as MaterialRow] }, EXAMPLE);
    if (!name) return null;
    return (
        <div className="space-y-1">
            <div className="field-label">Example name</div>
            <div className="name-preview rounded-(--field-radius) border border-line bg-surface px-(--field-px) py-(--field-py) whitespace-pre-line">
                <ServiceM8Name name={name} tags={nameTags} />
            </div>
        </div>
    );
}

/** A tag's or set's name over the most allowed. */
const tooLong = (v: string) => (v.trim().length > TAG_NAME_MAX ? `At most ${TAG_NAME_MAX} characters` : null);

/** A ready-made tag (a preset): its name, and what it puts in names. */
type Preset = { name: string; wording?: Wording };
const label = (name: string, kind: Wording["kind"], text = name): Preset => ({ name, wording: { kind, text } });

type PresetEntry = { set: string; oneOnly: boolean; tags: Preset[] } | { tag: Preset };

/**
 * Ready-made sets and tags (Quick add), for any business: each added to the draft, unless it has one by that name already (of
 * either kind). Items' are how they're sold; products' what they are (items linked to them have them too).
 */
const PRESETS: Record<"items" | "products", PresetEntry[]> = {
    items: [
        { set: "Scope", oneOnly: true, tags: [label("Installation", "includes"), label("Supply Only", "brackets"), label("Labour Only", "brackets")] },
        { set: "Warranty", oneOnly: true, tags: [label("12 Month Warranty", "brackets"), label("24 Month Warranty", "brackets")] },
        { set: "Status", oneOnly: true, tags: [label("Clearance", "brackets"), label("Discontinued", "brackets")] },
        ...["Parts", "Programming", "Delivery", "Disposal"].map((name) => ({ tag: label(name, "includes") })),
    ],
    products: [
        { set: "Unit", oneOnly: true, tags: [label("Per Metre", "brackets"), label("Per Hour", "brackets"), label("Per Bag", "brackets")] },
        { set: "Condition", oneOnly: true, tags: [label("New", "brackets"), label("Refurbished", "brackets"), label("Second Hand", "brackets")] },
        {
            set: "Special order",
            oneOnly: true,
            tags: ["3-7 business days", "10-15 business days", "unknown lead time"].map((lead) => label(`Special order (${lead})`, "line", `SPECIAL ORDER ITEM (${lead.toUpperCase()})`)),
        },
        ...["Batteries", "Keys", "Fixings"].map((name) => ({ tag: label(name, "includes") })),
    ],
};

/** Quick add's buttons: outlined in the colour of what they put in names (as the tags' chips are). */
const WORDING_QUICK: Record<Wording["kind"], string> = {
    brackets: "border-brand-500/40 text-brand-ink",
    includes: "border-brand-500/40 text-brand-ink",
    excludes: "border-brand-500/40 text-brand-ink",
    line: "border-warning/40 text-warning-ink",
};

/** A row changed in the draft (or new): a bar down its left edge, the editing colour. */
const CHANGED_ROW = "shadow-[inset_3px_0_0_var(--color-brand-500)]";
/** The tag or set open (its fields under it): its row and fields one block, tinted, with the bar down their left edge. */
const OPEN_ROW = "bg-surface-2 shadow-[inset_3px_0_0_var(--color-brand-500)]";

/** A row's drag handle (none: it can't be dragged now). */
function Handle({ label, sortable }: Readonly<{ label: string; sortable: ReturnType<typeof useSortable> | null }>) {
    if (!sortable) return null;
    return (
        <button
            type="button"
            className="-ml-1 flex shrink-0 cursor-grab touch-none items-center rounded-sm p-0.5 text-muted hover:text-ink active:cursor-grabbing"
            aria-label={`Move ${label}`}
            {...sortable.attributes}
            {...sortable.listeners}
        >
            <GripVertical className="size-4" />
        </button>
    );
}

/**
 * What a drag is over: what's under the pointer, the innermost first (a tag in a set before the set's tags' area, before the set),
 * the dragged one aside; nothing under it (the keyboard, or off the list), the closest.
 */
const underPointer: CollisionDetection = (args) => {
    const under = pointerWithin(args).filter((c) => c.id !== args.active.id);
    if (!under.length) return closestCenter(args);
    const area = (id: string | number) => {
        const r = args.droppableRects.get(id);
        return r ? r.width * r.height : Number.POSITIVE_INFINITY;
    };
    return under.toSorted((x, y) => area(x.id) - area(y.id));
};

// Dragged, its row stays where it would land, faded and tinted (its copy, under the pointer, is the DragOverlay).
const dragStyle = (s: ReturnType<typeof useSortable>): CSSProperties => ({ transform: CSS.Translate.toString(s.transform), transition: s.transition, opacity: s.isDragging ? 0.5 : undefined, position: "relative" });

export function TagsPanel({ data, disabled }: Readonly<{ data: PriceListData; disabled: boolean }>) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const saved = useMemo(() => listOf(data), [data.tags, data.tagSets]);
    const [draft, setDraft] = useState<DEntry[] | null>(null);
    const list = draft ?? saved;
    // Fields hold what's typed: started again on Cancel and Save.
    const [version, setVersion] = useState(0);
    const [open, setOpen] = useState<string | null>(null);
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const editable = !disabled && can(data, "settings");
    const searching = query.trim() !== "";
    // Every tag, items' and products', in one order (the order names put their wording in); narrowed to one kind to
    // look through, not to drag (the order is all of them).
    const [kindShown, setKindShown] = useState<"all" | "items" | "products">("all");
    // New ones: products' while only theirs are showing, else items'.
    const products = kindShown === "products";
    // The list as it was when the draft began: saved whole, the draft would undo whatever someone else saved since (asked first).
    const began = useRef<string | null>(null);
    // No draft (or changed back to what's saved): the next one begins afresh.
    if (draft === null) began.current = null;
    const update = (change: (list: DEntry[]) => DEntry[]) => {
        setError(null);
        began.current ??= JSON.stringify(saved);
        setDraft((was) => {
            const next = change(was ?? saved);
            return JSON.stringify(next) === JSON.stringify(saved) ? null : next;
        });
    };
    const patchTag = (key: string, patch: Partial<DTag>) =>
        update((l) => l.map((e) => ("tag" in e ? (e.tag.key === key ? { tag: { ...e.tag, ...patch } } : e) : { set: { ...e.set, tags: e.set.tags.map((t) => (t.key === key ? { ...t, ...patch } : t)) } })));
    const patchSet = (key: string, patch: Partial<DSet>) => update((l) => l.map((e) => ("set" in e && e.set.key === key ? { set: { ...e.set, ...patch } } : e)));
    const removeTag = (key: string) => update((l) => l.flatMap((e): DEntry[] => ("tag" in e ? (e.tag.key === key ? [] : [e]) : [{ set: { ...e.set, tags: e.set.tags.filter((t) => t.key !== key) } }])));
    // A set deleted: its tags stay, where it was.
    const removeSet = (key: string) => update((l) => l.flatMap((e): DEntry[] => ("set" in e && e.set.key === key ? e.set.tags.map((tag) => ({ tag })) : [e])));
    const freeName = (base: string, taken: string[]) => {
        let name = base;
        for (let n = 2; taken.some((t) => t.trim().toLowerCase() === name.toLowerCase()); n++) name = `${base} ${n}`;
        return name;
    };
    const addTag = (name: string, setKey: string | null) => {
        const tag: DTag = { key: newKey(), id: null, name: freeName(name, tagsIn(list).map((x) => x.tag.name)), wording: null, forProducts: products };
        update((l) => (setKey ? l.map((e) => ("set" in e && e.set.key === setKey ? { set: { ...e.set, tags: [...e.set.tags, tag] } } : e)) : [...l, { tag }]));
        setOpen(tag.key);
    };
    const addSet = (name: string) => {
        const set: DSet = { key: newKey(), id: null, name: freeName(name, setsIn(list).map((s) => s.name)), oneOnly: false, wording: null, forProducts: products, tags: [] };
        update((l) => [...l, { set }]);
        setOpen(set.key);
    };
    // Quick add: those it hasn't got a set or tag of that name for.
    const taken = (name: string, kind: "set" | "tag") => (kind === "set" ? setsIn(list).map((s) => s.name) : tagsIn(list).map((x) => x.tag.name)).some((n) => n.trim().toLowerCase() === name.toLowerCase());
    const quick = PRESETS[products ? "products" : "items"].filter((p) => ("set" in p ? !taken(p.set, "set") : !taken(p.tag.name, "tag")));
    const addPreset = (p: PresetEntry) => {
        const tag = (t: Preset): DTag => ({ key: newKey(), id: null, name: t.name, wording: t.wording ?? null, forProducts: products });
        if ("tag" in p) {
            const t = tag(p.tag);
            update((l) => [...l, { tag: t }]);
            setOpen(t.key);
            return;
        }
        // Its tags that are elsewhere already (by name) are left where they are.
        const set: DSet = { key: newKey(), id: null, name: p.set, oneOnly: p.oneOnly, wording: null, forProducts: products, tags: p.tags.filter((t) => !taken(t.name, "tag")).map(tag) };
        update((l) => [...l, { set }]);
        setOpen(set.key);
    };
    // A new one: brought into view, open.
    useEffect(() => {
        if (open?.startsWith("new:")) document.getElementById(`tag-row-${open}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [open]);

    // How many active items have each tag, and a set any of its tags (as saved).
    const counts = useMemo(() => {
        const out = new Map<string, number>();
        const setOf = new Map(data.tags.map((t) => [t.id, t.setId]));
        for (const m of data.materials) {
            if (!m.active) continue;
            // Its products' and group's too (productTags.ts).
            const ids = tagIdsOf(data, m.uuid);
            const sets = new Set(ids.flatMap((id) => setOf.get(id) ?? []));
            for (const id of [...ids, ...sets]) out.set(id, (out.get(id) ?? 0) + 1);
        }
        return out;
    }, [data]);
    const count = (key: string) => counts.get(key) ?? 0;

    const rows = useMemo(() => rowsOf(list, data), [list, data.tags, data.tagSets]);
    const savedTags = useMemo(() => new Map(tagsIn(saved).map((x) => [x.tag.key, x])), [saved]);
    const savedSets = useMemo(() => new Map(setsIn(saved).map((s) => [s.key, s])), [saved]);
    const tagChanged = (t: DTag, set: DSet | null) => {
        const was = savedTags.get(t.key);
        return !was || was.tag.name !== t.name || JSON.stringify(was.tag.wording) !== JSON.stringify(t.wording) || was.tag.forProducts !== t.forProducts || (was.set?.key ?? null) !== (set?.key ?? null);
    };
    const setChanged = (s: DSet) => {
        const was = savedSets.get(s.key);
        return !was || was.name !== s.name || was.oneOnly !== s.oneOnly || JSON.stringify(was.wording) !== JSON.stringify(s.wording) || was.forProducts !== s.forProducts;
    };

    // One-only sets that would give items more than one of their tags (as tagged now): by set, those items.
    const clashes = useMemo(() => {
        const out = new Map<string, MaterialRow[]>();
        const items = new Map(data.materials.map((m) => [m.uuid, m]));
        for (const s of setsIn(list)) {
            if (!s.oneOnly) continue;
            const several = havingSeveral(tagsByItem(data), s.tags.flatMap((t) => (t.id ? [t.id] : []))).flatMap((uuid) => items.get(uuid) ?? []);
            if (several.length) out.set(s.key, several);
        }
        return out;
    }, [list, data.itemTags, data.materials]);

    // What keeps the draft from being saved: first one said.
    const problem = useMemo(() => {
        const twice = (kind: string, names: string[]) => {
            const seen = new Set<string>();
            for (const n of names.map((x) => x.trim())) {
                if (!n) return `Every ${kind} needs a name.`;
                if (tooLong(n)) return `${n.slice(0, 20)}...: a ${kind} name is at most ${TAG_NAME_MAX} characters.`;
                if (seen.has(n.toLowerCase())) return `There's more than one ${kind} called ${n}.`;
                seen.add(n.toLowerCase());
            }
            return null;
        };
        const blank = [...tagsIn(list).map((x) => x.tag), ...setsIn(list)].find((x) => x.wording && !x.wording.text.trim());
        return (
            twice("tag", tagsIn(list).map((x) => x.tag.name)) ??
            twice("set", setsIn(list).map((s) => s.name)) ??
            (blank ? `${blank.name}: type what it adds to names, or set it to Nothing.` : null)
        );
    }, [list]);
    // Items that will need attention once it's saved: more than one tag of a pick-one set (marked on the set's row).
    const clashCount = new Set([...clashes.values()].flat().map((m) => m.uuid)).size;

    // Names that will read differently once it's saved (and don't now): each a name to update.
    const namesToUpdate = useMemo(() => {
        if (!draft) return 0;
        const ids = new Set(rows.tags.map((t) => t.id));
        const itemTags = Object.fromEntries(Object.entries(data.itemTags).map(([uuid, tags]) => [uuid, tags.filter((id) => ids.has(id))]));
        const next = { ...data, ...rows, itemTags, nameTags: deriveConfig(rows, data.nameTags.retired) };
        return data.materials.filter((m) => m.active && smartName(next, m) !== null && smartName(data, m) === null).length;
    }, [draft, rows, data]);

    // What saving does to items, said first: tags moved or taken off.
    const effects = () => {
        const out: string[] = [];
        for (const { tag } of tagsIn(list)) {
            const was = tag.id ? savedTags.get(tag.id) : undefined;
            const n = count(tag.key);
            if (was && was.tag.forProducts !== tag.forProducts && n) out.push(movedText(tag.name, tag.forProducts));
        }
        for (const s of setsIn(list)) {
            const was = s.id ? savedSets.get(s.id) : undefined;
            const n = count(s.key);
            if (was && was.forProducts !== s.forProducts && n) out.push(movedText(`The ${s.name} set's tags`, s.forProducts));
        }
        const kept = new Set(tagsIn(list).map((x) => x.tag.key));
        for (const [key, { tag }] of savedTags) if (!kept.has(key) && count(key)) out.push(`Deleting ${tag.name} takes it off its ${plural(count(key), "item")}.`);
        for (const [key, items] of clashes) {
            const s = setsIn(list).find((x) => x.key === key);
            if (s) out.push(`${plural(items.length, "item")} will have more than one ${s.name} tag: ${items.length === 1 ? "it needs" : "they need"} attention until the others are taken off.`);
        }
        return out;
    };
    const save = async () => {
        if (began.current !== null && began.current !== JSON.stringify(saved)) {
            const yes = await confirm({
                title: "Someone else changed the tags",
                message: "The tags were changed and saved by someone else since you started. Saving puts the whole list as you have it, undoing their changes. To keep them, cancel yours and start again.",
                confirmLabel: "Save mine anyway",
                tone: "danger",
            });
            if (!yes) return;
        }
        const said = effects();
        if (said.length) {
            const yes = await confirm({
                title: "Save the tags?",
                message: (
                    <ul className="m-0 list-disc space-y-1 pl-5">
                        {said.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                ),
                confirmLabel: "Save",
                hold: true,
            });
            if (!yes) return;
        }
        setBusy(true);
        setError(null);
        try {
            await saver.saveTagList(entriesOf(list));
            setDraft(null);
            setOpen(null);
            setVersion((v) => v + 1);
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };
    const cancel = () => {
        setDraft(null);
        setError(null);
        setOpen(null);
        setVersion((v) => v + 1);
    };

    // Dragging: sets among the rest; tags anywhere, into and out of sets (moved as they go over, ordered where they're let go).
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const containerOf = (l: readonly DEntry[], id: string): string | null => {
        if (id === ROOT) return ROOT;
        if (id.startsWith("in:")) return id.slice(3);
        if (l.some((e) => keyOf(e) === id)) return ROOT;
        return setsIn(l).find((s) => s.tags.some((t) => t.key === id))?.key ?? null;
    };
    const isSet = (l: readonly DEntry[], id: string) => setsIn(l).some((s) => s.key === id);
    // While dragging: the list as it would be dropped now (a tag moved into or out of a set as it goes over one, so the rows open
    // where it would land), and what's being dragged (its copy follows the pointer). Only the draft changes, once, when it's let go.
    const [dragging, setDragging] = useState<{ id: string; list: DEntry[] } | null>(null);
    // Just moved to another set (or out of one): not again until the rows have moved (else it flips back and forth at an edge).
    const justMoved = useRef(false);
    useEffect(() => {
        if (!justMoved.current) return;
        const frame = requestAnimationFrame(() => (justMoved.current = false));
        return () => cancelAnimationFrame(frame);
    }, [dragging]);
    // A tag over another set's tags (or its area), or out among the rest (over a set's row: before it): moved there now.
    const onDragOver = ({ active, over }: DragOverEvent) => {
        if (!over || !dragging || justMoved.current) return;
        const id = String(active.id);
        const overId = String(over.id);
        const l = dragging.list;
        if (isSet(l, id) || id === overId) return;
        const from = containerOf(l, id);
        const to = isSet(l, overId) ? ROOT : containerOf(l, overId);
        if (!from || !to || from === to) return;
        justMoved.current = true;
        setDragging({ id, list: moveTag(l, id, to, overId.startsWith("in:") ? null : overId) });
    };
    // Let go: its place among the rest (or in its set), from where it was taken to, then saved to the draft.
    const onDragEnd = ({ active, over }: DragEndEvent) => {
        const l = dragging?.list ?? list;
        setDragging(null);
        const id = String(active.id);
        const overId = over ? String(over.id) : null;
        const placed = (() => {
            if (!overId || id === overId) return l;
            if (isSet(l, id)) {
                // A set goes among the rest: over a tag in a set (or its tags' area), next to that set.
                const c = containerOf(l, overId);
                const target = c && c !== ROOT ? c : overId;
                const from = l.findIndex((e) => keyOf(e) === id);
                const to = l.findIndex((e) => keyOf(e) === target);
                return from < 0 || to < 0 ? l : arrayMove(l, from, to);
            }
            const from = containerOf(l, id);
            const to = isSet(l, overId) ? ROOT : containerOf(l, overId);
            if (!from || !to || overId.startsWith("in:")) return l;
            if (from !== to) return moveTag(l, id, to, overId);
            if (to === ROOT) {
                const i = l.findIndex((e) => keyOf(e) === id);
                const j = l.findIndex((e) => keyOf(e) === overId);
                return i < 0 || j < 0 ? l : arrayMove(l, i, j);
            }
            return l.map((e) => {
                if (!("set" in e) || e.set.key !== to) return e;
                const i = e.set.tags.findIndex((t) => t.key === id);
                const j = e.set.tags.findIndex((t) => t.key === overId);
                return i < 0 || j < 0 ? e : { set: { ...e.set, tags: arrayMove(e.set.tags, i, j) } };
            });
        })();
        update(() => placed);
    };
    // What's dragged, for its copy under the pointer: a set's name, or a tag's.
    const draggedName = (() => {
        if (!dragging) return null;
        const set = setsIn(dragging.list).find((s) => s.key === dragging.id);
        if (set) return { set: true, name: set.name || "Untitled set" };
        return { set: false, name: tagsIn(dragging.list).find((x) => x.tag.key === dragging.id)?.tag.name || "Untitled" };
    })();

    // Searched: tags whose name or wording match, and sets whose name does (with all their tags) or any of whose tags do.
    const matches = (t: DTag) => matchesSearch(`${t.name} ${t.wording ? wordingText(t.wording) : ""}`, query);
    // The kind it's narrowed to (all: every one).
    const mine = (e: DEntry) => kindShown === "all" || ("tag" in e ? e.tag.forProducts : e.set.forProducts) === (kindShown === "products");
    const shown = (dragging?.list ?? list).filter(mine).flatMap((e): DEntry[] => {
        if ("tag" in e) return matches(e.tag) ? [e] : [];
        if (matchesSearch(e.set.name, query)) return [e];
        const tags = e.set.tags.filter(matches);
        return tags.length ? [{ set: { ...e.set, tags } }] : [];
    });
    const canDrag = editable && !searching && !busy && kindShown === "all";

    const tagItem = (t: DTag, set: DSet | null) => (
        <SortableRow key={t.key} id={t.key} canDrag={canDrag}>
            {(sortable) => (
                <>
                    <div id={`tag-row-${t.key}`} className={clsx("flex items-center gap-2 py-1.5 pr-3 pl-3", open === t.key ? OPEN_ROW : tagChanged(t, set) && CHANGED_ROW)}>
                        <Handle label={t.name || "tag"} sortable={sortable} />
                        <button type="button" className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left" onClick={() => setOpen(open === t.key ? null : t.key)} aria-expanded={open === t.key}>
                            {/* In a set, the set's kind is its. */}
                            {set ? null : <KindMark forProducts={t.forProducts} />}
                            <TagChip
                                // Its set, as drafted.
                                data={{ tagSets: set ? [{ id: set.key, name: set.name, oneOnly: set.oneOnly, wording: set.wording, forProducts: set.forProducts, aliases: [], position: 0 }] : [] }}
                                tag={{ id: t.key, name: t.name || "Untitled", setId: set?.key ?? null, wording: t.wording, forProducts: t.forProducts, aliases: [], position: 0 }}
                            />
                            {t.wording?.text.trim() ? <span className={clsx("truncate text-xs", WORDING_TEXT[t.wording.kind])}>{wordingText(t.wording)}</span> : null}
                            {t.id ? null : <Badge tone="green">New</Badge>}
                            <span className="ml-auto shrink-0 text-xs text-muted tabular-nums">{plural(count(t.key), "item")}</span>
                        </button>
                    </div>
                    {open === t.key ? (
                        <Card>
                            <DraftFields key={`${t.key}:${version}`}>
                                <div className="grid gap-3 sm:grid-cols-3">
                                    <TextField label="Tag name" value={t.name} disabled={!editable} live validate={tooLong} onSave={async (name) => patchTag(t.key, { name })} />
                                    <WordingFields wording={t.wording} name={t.name} disabled={!editable} onChange={(wording) => patchTag(t.key, { wording })} />
                                </div>
                                <ExampleName rows={rows} retired={data.nameTags.retired} tagKey={t.key} />
                            </DraftFields>
                            {editable ? (
                                <CardActions>
                                    {/* In a set, the set says where its tags go. */}
                                    {set ? null : <MoveButton forProducts={t.forProducts} onMove={(forProducts) => patchTag(t.key, { forProducts })} />}
                                    <button type="button" className="btn-secondary" onClick={() => removeTag(t.key)}>
                                        <Trash2 className="size-4" /> Delete tag
                                    </button>
                                </CardActions>
                            ) : null}
                        </Card>
                    ) : null}
                </>
            )}
        </SortableRow>
    );

    const setItem = (s: DSet) => {
        const clash = clashes.get(s.key);
        return (
            <SortableRow key={s.key} id={s.key} canDrag={canDrag}>
                {(sortable) => (
                    <>
                        <div id={`tag-row-${s.key}`} className={clsx("flex items-center gap-2 px-3 py-1.5", open === s.key ? OPEN_ROW : setChanged(s) && CHANGED_ROW)}>
                            <Handle label={s.name || "set"} sortable={sortable} />
                            <button type="button" className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left" onClick={() => setOpen(open === s.key ? null : s.key)} aria-expanded={open === s.key}>
                                <KindMark forProducts={s.forProducts} />
                                <FolderOpen className="size-4 shrink-0 text-muted" />
                                <span className={open === s.key ? "font-semibold" : "font-medium"}>{s.name || "Untitled set"}</span>
                                {/* Only the sets that behave differently are marked: any number is the usual. */}
                                {s.oneOnly ? <Badge title="An item can have only one of these: picking another swaps it">Pick one</Badge> : null}
                                {s.wording?.text.trim() ? <span className={clsx("truncate text-xs", WORDING_TEXT[s.wording.kind])}>+ {wordingText(s.wording)}</span> : null}
                                {s.id ? null : <Badge tone="green">New</Badge>}
                            </button>
                            {/* Items with more than one of its tags (it allows one): just them, in the item list. */}
                            {clash ? (
                                <Link
                                    href={`/t/${meta.id}/materials?items=${clash.map((m) => m.uuid).join(",")}`}
                                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-danger-soft px-2.5 py-0.5 text-xs font-medium text-danger no-underline"
                                    title={`${plural(clash.length, "item")} with more than one of its tags (it allows one): see them in the item list, and take the others off`}
                                >
                                    <TriangleAlert className="size-3.5" /> {plural(clash.length, "conflict")}
                                </Link>
                            ) : null}
                            {/* Its count after the button, so a conflict sits before it (not inside the row's button). */}
                            <span className="shrink-0 text-xs text-muted tabular-nums">{plural(count(s.key), "item")}</span>
                        </div>
                        {open === s.key ? (
                            <Card>
                                <DraftFields key={`${s.key}:${version}`}>
                                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                        <TextField label="Set name" value={s.name} disabled={!editable} live validate={tooLong} onSave={async (name) => patchSet(s.key, { name })} />
                                        <SelectField
                                            label="Items can have"
                                            value={s.oneOnly ? "one" : "any"}
                                            disabled={!editable}
                                            options={[
                                                { value: "any", label: "Any of its tags" },
                                                { value: "one", label: "Only one of its tags" },
                                            ]}
                                            hint={s.oneOnly ? "Picking another swaps it." : "Any number of them."}
                                            onSave={async (v) => patchSet(s.key, { oneOnly: v === "one" })}
                                        />
                                        <WordingFields wording={s.wording} name={s.name} disabled={!editable} hint="When an item has any of its tags." onChange={(wording) => patchSet(s.key, { wording })} />
                                    </div>
                                    <ExampleName rows={rows} retired={data.nameTags.retired} setKey={s.key} />
                                </DraftFields>
                                {editable ? (
                                    <CardActions>
                                        <button type="button" className="btn-secondary" onClick={() => addTag("New tag", s.key)}>
                                            <Plus className="size-4" /> Add tag
                                        </button>
                                        <MoveButton forProducts={s.forProducts} onMove={(forProducts) => patchSet(s.key, { forProducts })} />
                                        <button type="button" className="btn-secondary" onClick={() => removeSet(s.key)} title="Its tags stay, where it was">
                                            <Trash2 className="size-4" /> Delete set
                                        </button>
                                    </CardActions>
                                ) : null}
                            </Card>
                        ) : null}
                        {/* Its tags inside it: indented, along a rail, so the tags after the set are plainly not in it; tags dragged in here go in it. */}
                        <SetTags setKey={s.key} ids={s.tags.map((t) => t.key)}>
                            {s.tags.map((t) => tagItem(t, s))}
                            {s.tags.length ? null : <li className="py-1.5 pl-3 text-sm text-muted">No tags in it yet: drag one here, or open the set to add one.</li>}
                        </SetTags>
                    </>
                )}
            </SortableRow>
        );
    };

    const changes = draft ? summary(list, saved) : "";
    return (
        <Panel title="Tags" id="tags" className={draft ? "overflow-visible" : undefined}>
            <LeaveGuard when={draft !== null} message="Your changes to tags aren't saved yet." />
            {/* The same list in Price List and Catalogue settings: one order, the order names put their wording in. */}
            <p className="m-0 mb-3 text-sm text-muted">
                Every tag, in the order item names use. <KindMark forProducts={false} inline /> Item tags are how an item is sold (Supply only, Installation);{" "}
                <KindMark forProducts inline /> product tags are what a product is (Keyway, Brand), and items linked to it have them too. New ones here are{" "}
                item tags (product tags while Products is showing). Drag to reorder, and into or out of sets.
            </p>
            <div className="overflow-clip rounded-md border border-line">
                {/* Along its top: a search, which is also the name of a new tag or set. */}
                <form
                    className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-2 px-3 py-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!editable || !query.trim()) return;
                        addTag(query.trim(), null);
                        setQuery("");
                    }}
                >
                    <label className="relative block min-w-48 flex-1">
                        <span className="sr-only">Search tags, or name a new one</span>
                        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                        <input className="input pl-9" placeholder={editable ? "Search, or name a new tag" : "Search tags"} maxLength={TAG_NAME_MAX} value={query} onChange={(e) => setQuery(e.target.value)} />
                    </label>
                    {/* One kind to look through (dragging is for all of them: their order is one). */}
                    <div role="group" aria-label="Show" className="inline-flex shrink-0 rounded-md border border-line bg-surface p-0.5 text-xs">
                        {(["all", "items", "products"] as const).map((k) => (
                            <button
                                key={k}
                                type="button"
                                aria-pressed={kindShown === k}
                                className={clsx("cursor-pointer rounded px-2 py-1", kindShown === k ? "bg-surface-2 font-semibold text-ink" : "text-muted hover:text-ink")}
                                onClick={() => setKindShown(k)}
                            >
                                {k === "all" ? "All" : k === "items" ? "Items" : "Products"}
                            </button>
                        ))}
                    </div>
                    {editable ? (
                        <>
                            <button type="submit" className="btn-secondary shrink-0" disabled={!query.trim()} title={query.trim() ? `Add a tag called ${query.trim()}` : "Type its name first"}>
                                <Plus className="size-4" /> Add tag
                            </button>
                            <button
                                type="button"
                                className="btn-secondary shrink-0"
                                disabled={!query.trim()}
                                title={query.trim() ? `Add a set called ${query.trim()}` : "Type its name first"}
                                onClick={() => {
                                    addSet(query.trim());
                                    setQuery("");
                                }}
                            >
                                <Plus className="size-4" /> Add set
                            </button>
                        </>
                    ) : null}
                </form>
                {shown.length ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={underPointer}
                        // Measured again as rows move (a tag moved into a set while dragging), so what's under the pointer is right.
                        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
                        onDragStart={({ active }) => setDragging({ id: String(active.id), list })}
                        onDragOver={onDragOver}
                        onDragEnd={onDragEnd}
                        onDragCancel={() => setDragging(null)}
                    >
                        <SortableContext id={ROOT} items={(dragging?.list ?? list).map(keyOf)} strategy={verticalListSortingStrategy}>
                            <ul className="divide-y divide-line">{shown.map((e) => ("set" in e ? setItem(e.set) : tagItem(e.tag, null)))}</ul>
                        </SortableContext>
                        {/* Its copy, under the pointer (its row stays faded where it would land). */}
                        <DragOverlay dropAnimation={null}>
                            {draggedName ? (
                                <div className="flex items-center gap-2 rounded-md border border-brand-500 bg-surface px-3 py-1.5 text-sm font-medium shadow-lg">
                                    <GripVertical className="size-4 text-muted" />
                                    {draggedName.set ? <FolderOpen className="size-4 text-muted" /> : null}
                                    {draggedName.name}
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    <p className="m-0 px-3 py-2.5 text-sm text-muted">{list.length ? (searching ? "No tags match." : `No ${kindShown === "products" ? "product" : "item"} tags yet.`) : "No tags yet."}</p>
                )}
            </div>
            {/* Ready-made ones it hasn't got (by name): each into the draft, opened. */}
            {editable && quick.length ? (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="mr-1 text-xs text-muted">Quick add:</span>
                    {quick.map((p) => {
                        const name = "set" in p ? p.set : p.tag.name;
                        const kind = "set" in p ? p.tags[0]?.wording?.kind : p.tag.wording?.kind;
                        return (
                            <button
                                key={name}
                                type="button"
                                className={clsx("inline-flex cursor-pointer items-center gap-1 rounded-full border bg-surface px-2.5 py-0.5 text-xs font-medium hover:bg-surface-2", kind ? WORDING_QUICK[kind] : "border-line text-muted hover:text-ink")}
                                title={"set" in p ? `A set${p.oneOnly ? " (pick one)" : ""}: ${p.tags.map((t) => t.name).join(", ")}` : p.tag.wording ? `In the name: ${wordingText(p.tag.wording)}` : "A label"}
                                onClick={() => addPreset(p)}
                            >
                                {"set" in p ? <FolderOpen className="size-3.5" /> : <Plus className="size-3" />}
                                {name}
                            </button>
                        );
                    })}
                </div>
            ) : null}
            {/* The draft's save bar: kept in view along the bottom while there's one. */}
            {draft ? (
                <div className="sticky bottom-0 z-20 -mx-5 mt-3 -mb-5 border-t border-brand-500/30 bg-surface px-5 py-3 shadow-[0_-4px_12px_-8px_rgb(0_0_0/0.25)]">
                    <DraftBar
                        message={
                            problem ? (
                                <span className="text-danger">{problem}</span>
                            ) : (
                                <>
                                    {changes}
                                    {namesToUpdate ? ` ${plural(namesToUpdate, "item")} will show their names to update.` : ""}
                                    {clashCount ? <span className="text-warning-ink"> {plural(clashCount, "item")} will have more than one tag of a pick-one set.</span> : null}
                                </>
                            )
                        }
                        busy={busy}
                        error={error}
                        actions={
                            <>
                                <button type="button" className="btn-primary" disabled={busy || problem !== null} onClick={() => void save()}>
                                    Save
                                </button>
                                <button type="button" className="btn-secondary" disabled={busy} onClick={cancel}>
                                    Cancel
                                </button>
                            </>
                        }
                    />
                </div>
            ) : null}
        </Panel>
    );
}

/** What the draft changes, in a few words: "2 tags added, 1 changed, 1 set deleted, order changed." */
function summary(list: readonly DEntry[], saved: readonly DEntry[]) {
    const was = new Map(tagsIn(saved).map((x) => [x.tag.key, x]));
    const wasSets = new Map(setsIn(saved).map((s) => [s.key, s]));
    const now = tagsIn(list);
    const nowSets = setsIn(list);
    const tagKeys = new Set(now.map((x) => x.tag.key));
    const setKeys = new Set(nowSets.map((s) => s.key));
    const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    const parts = [
        [now.filter((x) => !x.tag.id).length, "tag", "added"],
        [now.filter((x) => x.tag.id && !same({ ...was.get(x.tag.key)?.tag, s: was.get(x.tag.key)?.set?.key ?? null }, { ...x.tag, s: x.set?.key ?? null })).length, "tag", "changed"],
        [[...was.keys()].filter((k) => !tagKeys.has(k)).length, "tag", "deleted"],
        [nowSets.filter((s) => !s.id).length, "set", "added"],
        [nowSets.filter((s) => s.id && !same({ ...wasSets.get(s.key), tags: [] }, { ...s, tags: [] })).length, "set", "changed"],
        [[...wasSets.keys()].filter((k) => !setKeys.has(k)).length, "set", "deleted"],
    ] as const;
    const said = parts.filter(([n]) => n > 0).map(([n, kind, what]) => `${n} ${n === 1 ? kind : `${kind}s`} ${what}`);
    const order = (l: readonly DEntry[]) => l.flatMap((e) => ("set" in e ? [e.set.key, ...e.set.tags.map((t) => t.key)] : [e.tag.key]));
    // Those in both, in another order.
    if (!same(order(list).filter((k) => !k.startsWith("new:")), order(saved).filter((k) => tagKeys.has(k) || setKeys.has(k)))) said.push("order changed");
    const text = said.join(", ") || "changes";
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

/** A row that can be dragged by its handle (`canDrag`), a tag's or a set's (its tags with it). */
function SortableRow({ id, canDrag, children }: Readonly<{ id: string; canDrag: boolean; children: (sortable: ReturnType<typeof useSortable> | null) => ReactNode }>) {
    const sortable = useSortable({ id, disabled: !canDrag });
    return (
        <li ref={sortable.setNodeRef} style={dragStyle(sortable)} className={clsx(sortable.isDragging && "bg-brand-500/10")}>
            {children(canDrag ? sortable : null)}
        </li>
    );
}

/** A set's tags: their own sortable list, and somewhere to drop a tag into it (empty too). */
function SetTags({ setKey, ids, children }: Readonly<{ setKey: string; ids: string[]; children: ReactNode }>) {
    const { setNodeRef, isOver } = useDroppable({ id: `in:${setKey}` });
    return (
        <SortableContext id={`in:${setKey}`} items={ids} strategy={verticalListSortingStrategy}>
            <ul ref={setNodeRef} className={clsx("mb-2 ml-5 border-l-2", isOver ? "border-brand-500" : "border-line")}>
                {children}
            </ul>
        </SortableContext>
    );
}

/** An opened tag or set: its fields, then its buttons along the bottom, one block with its row (OPEN_ROW): the same tint and bar, no line between. */
const Card = ({ children }: Readonly<{ children: ReactNode }>) => <div className={clsx("space-y-4 px-3 pt-1 pb-3", OPEN_ROW)}>{children}</div>;

function CardActions({ children }: Readonly<{ children: ReactNode }>) {
    return (
        <div className="-mx-3 flex flex-wrap items-center justify-between gap-3 border-t border-line px-3 pt-3">
            <div className="ml-auto flex flex-wrap gap-2">{children}</div>
        </div>
    );
}

/**
 * Changing a set (or loose tag) to the other kind, in the draft (pressed again, back): from items' (how one's sold: Supply only) to
 * products' (what one is: Keyway), which items linked to them then have too (productTags.ts), or back. Saving moves linked items'
 * tags to their products, or back.
 */
function MoveButton({ forProducts, onMove }: Readonly<{ forProducts: boolean; onMove: (forProducts: boolean) => void }>) {
    return (
        <button
            type="button"
            className="btn-secondary"
            title={forProducts ? "Its tags go on items instead (how an item is sold)" : "Its tags go on products instead (what a product is): items linked to them have them too"}
            onClick={() => onMove(!forProducts)}
        >
            <ArrowRightLeft className="size-4" /> {forProducts ? "Make it for items" : "Make it for products"}
        </button>
    );
}

/** Which kind a set (or loose tag) is: items' (the item's box, blue) or products' (the product's puzzle piece, rose), as everywhere. */
function KindMark({ forProducts, inline = false }: Readonly<{ forProducts: boolean; /** In a sentence: sized and placed as text. */ inline?: boolean }>) {
    const Icon = forProducts ? Puzzle : Package;
    return (
        <Icon
            className={clsx(inline ? "inline size-3.5 align-[-2px]" : "size-4 shrink-0", TONE_TEXT[forProducts ? "rose" : "blue"])}
            aria-label={forProducts ? "Product tag" : "Item tag"}
        >
            <title>{forProducts ? "Product tag: what a product is. Items linked to it have it too." : "Item tag: how an item is sold."}</title>
        </Icon>
    );
}

/** What saving a change of where tags go does to items. */
const movedText = (what: string, toProducts: boolean) =>
    toProducts ? `${what} will go on products: linked items' move to their products, and they keep having them from there.` : `${what} will go on items: products' move to the items linked to them.`;


